import type { ApolloClient } from '@apollo/client'

import type { RequisitionProfile, VacancyProfile } from '@factorialco/f0-react'
import {
  EntityResolverCandidateDocument,
  EntityResolverJobPostingDocument,
  EntityResolverRequisitionDocument,
  EntityResolverVacancyDocument,
} from 'generated/resources/graphql'
import type { EntityRefResolver, EntityUrlBuilder } from 'modules/core/lib/entityRefs/types'

// TODO: Remove local mirrors once f0 PR #3981 is released and bumped (https://github.com/factorialco/f0/pull/3981)
type CandidateProfile = {
  id: string
  firstName: string
  lastName: string
  avatarUrl?: string
  source?: string
}

type JobPostingProfile = {
  id: string
  title: string
  status?: string
  location?: string
}

// URL builders

export const candidateUrl: EntityUrlBuilder = (id, navigation) =>
  navigation.ats.candidates.id.fullPath({ id })

export const jobPostingUrl: EntityUrlBuilder = (id, navigation) =>
  navigation.ats.jobs.jobsId.fullPath({ jobId: id })

export const requisitionUrl: EntityUrlBuilder = (id, navigation) =>
  navigation.ats.hiringPlan.requisitions.requisitionDetail.fullPath({
    requisition_id: id,
  })

export const vacancyUrl: EntityUrlBuilder = (id, navigation) =>
  navigation.ats.hiringPlan.vacancies.vacancyDetail.fullPath({
    vacancy_id: id,
  })

// By-id resolvers

export const candidateResolver: EntityRefResolver<CandidateProfile> = async (
  client: ApolloClient<object>,
  id: string
) => {
  const { data } = await client.query({
    query: EntityResolverCandidateDocument,
    variables: { ids: [id] },
  })
  const node = data.ats.candidatesConnection.nodes[0]
  if (!node) throw new Error(`Candidate with id ${id} not found`)
  return {
    id: node.id,
    firstName: node.firstName,
    lastName: node.lastName,
    source: node.candidateSource?.label ?? undefined,
  }
}

export const jobPostingResolver: EntityRefResolver<JobPostingProfile> = async (
  client: ApolloClient<object>,
  id: string
) => {
  const { data } = await client.query({
    query: EntityResolverJobPostingDocument,
    variables: { ids: [id] },
  })
  const node = data.ats.jobPostingsConnection.nodes[0]
  if (!node) throw new Error(`Job posting with id ${id} not found`)
  return {
    id: node.id,
    title: node.title,
    status: node.status,
    location: node.location?.name ?? undefined,
  }
}

export const requisitionResolver: EntityRefResolver<RequisitionProfile> = async (
  client: ApolloClient<object>,
  id: string
) => {
  const { data } = await client.query({
    query: EntityResolverRequisitionDocument,
    variables: { ids: [id] },
  })
  const node = data.ats.requisitionsConnection.nodes[0]
  if (!node) throw new Error(`Requisition with id ${id} not found`)
  return {
    id: node.id,
    title: node.jobPostingTitle,
    status: node.status,
    reason: node.reason,
  }
}

export const vacancyResolver: EntityRefResolver<VacancyProfile> = async (
  client: ApolloClient<object>,
  id: string
) => {
  const { data } = await client.query({
    query: EntityResolverVacancyDocument,
    variables: { ids: [id] },
  })
  const node = data.ats.vacanciesConnection.nodes[0]
  if (!node) throw new Error(`Vacancy with id ${id} not found`)
  return {
    id: node.id,
    name: node.name,
    status: node.status,
    vacancyType: node.originTypeLabel,
  }
}
